/**
 * Shipping Verdict Panel
 *
 * Standalone widget that takes an HS code, prompts for a destination
 * country, and renders the EU TARIC shipping-compliance verdict.
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Public as PublicIcon,
  Block as BlockIcon,
  ReportProblem as WarningIcon,
  CheckCircle as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DocIcon,
  Gavel as LegalIcon,
  Science as DualUseIcon,
  Shield as MilitaryIcon,
  Flag as SanctionsIcon,
  Museum as CulturalIcon,
  Healing as TortureIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  checkShipping,
  ShippingCheckResponse,
  ShippingVerdictLevel,
  MeasureDetail,
  DocumentDetail,
  Caveat,
} from '../../../services/tariffClassifierService';
import { useTranslation } from '../../../store/languageStore';

interface ShippingVerdictPanelProps {
  hsCode: string;
}

type TFn = (key: string, params?: Record<string, any>) => string | string[];

const verdictColor: Record<ShippingVerdictLevel, 'success' | 'warning' | 'error'> = {
  PROHIBITED: 'error',
  CONDITIONS_APPLY: 'warning',
  CLEAR: 'success',
};

const verdictIcon: Record<ShippingVerdictLevel, React.ElementType> = {
  PROHIBITED: BlockIcon,
  CONDITIONS_APPLY: WarningIcon,
  CLEAR: ClearIcon,
};

const verdictKey: Record<ShippingVerdictLevel, { label: string; helper: string }> = {
  PROHIBITED: {
    label: 'dashboard.cards.tariffClassifier.shippingCompliance.verdict.prohibited',
    helper: 'dashboard.cards.tariffClassifier.shippingCompliance.verdict.prohibitedHelper',
  },
  CONDITIONS_APPLY: {
    label: 'dashboard.cards.tariffClassifier.shippingCompliance.verdict.conditionsApply',
    helper: 'dashboard.cards.tariffClassifier.shippingCompliance.verdict.conditionsApplyHelper',
  },
  CLEAR: {
    label: 'dashboard.cards.tariffClassifier.shippingCompliance.verdict.clear',
    helper: 'dashboard.cards.tariffClassifier.shippingCompliance.verdict.clearHelper',
  },
};

// Footnote-code prefixes → regulatory category. Pulled from TARIC's own
// taxonomy (DU=Dual Use, MG=Missile/Military Goods, TM=Territorial /
// sanctions Measures, CG=Cultural Goods, TR=Torture/Riot control).
type CategoryCode =
  | 'DUAL_USE'
  | 'MILITARY'
  | 'SANCTIONS'
  | 'CULTURAL_GOODS'
  | 'TORTURE_RIOT'
  | 'PROHIBITION';

const CATEGORY_BY_PREFIX: Record<string, CategoryCode> = {
  DU: 'DUAL_USE',
  MG: 'MILITARY',
  TM: 'SANCTIONS',
  CG: 'CULTURAL_GOODS',
  TR: 'TORTURE_RIOT',
};

const CATEGORY_ICON: Record<CategoryCode, React.ElementType> = {
  DUAL_USE: DualUseIcon,
  MILITARY: MilitaryIcon,
  SANCTIONS: SanctionsIcon,
  CULTURAL_GOODS: CulturalIcon,
  TORTURE_RIOT: TortureIcon,
  PROHIBITION: BlockIcon,
};

const CATEGORY_COLOR: Record<CategoryCode, 'error' | 'warning' | 'default'> = {
  DUAL_USE: 'warning',
  MILITARY: 'error',
  SANCTIONS: 'error',
  CULTURAL_GOODS: 'warning',
  TORTURE_RIOT: 'error',
  PROHIBITION: 'error',
};

const CATEGORY_LABEL_KEY: Record<CategoryCode, string> = {
  DUAL_USE: 'dashboard.cards.tariffClassifier.shippingCompliance.category.dualUse',
  MILITARY: 'dashboard.cards.tariffClassifier.shippingCompliance.category.military',
  SANCTIONS: 'dashboard.cards.tariffClassifier.shippingCompliance.category.sanctions',
  CULTURAL_GOODS: 'dashboard.cards.tariffClassifier.shippingCompliance.category.culturalGoods',
  TORTURE_RIOT: 'dashboard.cards.tariffClassifier.shippingCompliance.category.tortureRiot',
  PROHIBITION: 'dashboard.cards.tariffClassifier.shippingCompliance.category.prohibition',
};

function deriveCategories(result: ShippingCheckResponse): CategoryCode[] {
  const present = new Set<CategoryCode>();
  const allMeasures = [...result.import_restrictions, ...result.export_restrictions];
  for (const m of allMeasures) {
    if (m.series === 'A') present.add('PROHIBITION');
    for (const fn of m.footnotes || []) {
      const prefix = fn.code.slice(0, 2);
      const cat = CATEGORY_BY_PREFIX[prefix];
      if (cat) present.add(cat);
    }
    // Fallback: keyword match on measure-type description for cases where
    // a measure has no attached footnotes (e.g. 478 "Export authorization
    // (Dual use)" without DU footnotes attached).
    const desc = (m.measure_type_description || '').toLowerCase();
    if (desc.includes('dual use') || desc.includes('dual-use')) present.add('DUAL_USE');
    if (desc.includes('common military list') || desc.includes('cml')) present.add('MILITARY');
  }
  // Stable order: prohibitions first, then most-restrictive controls.
  const order: CategoryCode[] = [
    'PROHIBITION',
    'MILITARY',
    'SANCTIONS',
    'DUAL_USE',
    'TORTURE_RIOT',
    'CULTURAL_GOODS',
  ];
  return order.filter((c) => present.has(c));
}

const MeasureList: React.FC<{ title: string; measures: MeasureDetail[]; t: TFn }> = ({
  title,
  measures,
  t,
}) => {
  if (!measures.length) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {title}:{' '}
          {t('dashboard.cards.tariffClassifier.shippingCompliance.noneApplicable') as string}
        </Typography>
      </Box>
    );
  }
  return (
    <Accordion
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" fontWeight={600}>
          {title} ({measures.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {measures.map((m, idx) => {
            const conditionsLabel =
              m.conditions_count === 1
                ? (t('dashboard.cards.tariffClassifier.shippingCompliance.conditionsCountSingular') as string)
                : (t('dashboard.cards.tariffClassifier.shippingCompliance.conditionsCountPlural', {
                    count: m.conditions_count,
                  }) as string);
            return (
              <Paper
                key={`${m.measure_type_id}-${idx}`}
                variant="outlined"
                sx={{ p: 1.25, borderRadius: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={
                      t('dashboard.cards.tariffClassifier.shippingCompliance.seriesLabel', {
                        series: m.series,
                      }) as string
                    }
                    size="small"
                    color={m.series === 'A' ? 'error' : 'warning'}
                    sx={{ fontWeight: 700 }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {
                      t('dashboard.cards.tariffClassifier.shippingCompliance.typeLabel', {
                        id: m.measure_type_id,
                      }) as string
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {m.geographical_area}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {m.measure_type_description}
                </Typography>
                {m.conditions_count > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {conditionsLabel}
                  </Typography>
                )}
                {(() => {
                  if (!m.footnotes || m.footnotes.length === 0) return null;
                  // Split footnotes:
                  //   substantive (CD*/TM*/TR*/CG*) carry regulatory text → render inline
                  //   list-refs (DU*/MG*) are just pointers into Annex I of
                  //     Reg 2021/821 etc., 10-20 per measure is normal → render
                  //     as compact comma-separated chips with hover tooltips
                  const substantive = m.footnotes.filter(
                    (fn) => !/^(DU|MG)/.test(fn.code),
                  );
                  const listRefs = m.footnotes.filter((fn) => /^(DU|MG)/.test(fn.code));
                  return (
                    <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'warning.light' }}>
                      {substantive.map((fn) => (
                        <Box key={fn.code} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" fontWeight={700} color="warning.dark">
                            {fn.code}
                          </Typography>
                          {fn.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', ml: 0.5, fontStyle: 'italic' }}
                            >
                              {fn.description}
                            </Typography>
                          )}
                        </Box>
                      ))}
                      {listRefs.length > 0 && (
                        <Box sx={{ mt: substantive.length > 0 ? 0.75 : 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            List refs:
                          </Typography>
                          {listRefs.map((fn) => (
                            <Tooltip
                              key={fn.code}
                              title={fn.description || fn.code}
                              arrow
                              placement="top"
                            >
                              <Chip
                                label={fn.code}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 20,
                                  cursor: 'help',
                                  fontFamily: 'monospace',
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Paper>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const ShippingVerdictPanel: React.FC<ShippingVerdictPanelProps> = ({ hsCode }) => {
  const { t } = useTranslation();
  const tr = t as TFn;
  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ShippingCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isValidCountry = /^[A-Za-z]{2}$/.test(destination);

  const handleCheck = async () => {
    if (!isValidCountry) {
      setError(
        tr('dashboard.cards.tariffClassifier.shippingCompliance.invalidCountry') as string,
      );
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await checkShipping({
        hs_code: hsCode,
        destination_country: destination.toUpperCase(),
      });
      setResult(res);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError(
          tr('dashboard.cards.tariffClassifier.shippingCompliance.commodityNotFound', {
            code: hsCode,
          }) as string,
        );
      } else {
        // FastAPI 422 returns detail as an array of validation errors;
        // 5xx / other usually returns a string.
        const detail = err?.response?.data?.detail;
        const message =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail) && detail[0]?.msg
              ? detail[0].msg
              : (tr('dashboard.cards.tariffClassifier.shippingCompliance.genericError') as string);
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Verdict uses its NATIVE semantic color (green/amber/red) — the
  // amber caveat alert below it carries the warning. Mixing both into
  // one amber blob made the panel read as a single contradictory
  // "Clear-in-warning-red" signal.
  const hasCaveats = !!(result && result.caveats && result.caveats.length > 0);
  const color = result ? verdictColor[result.verdict] : null;
  const VerdictIcon = result ? verdictIcon[result.verdict] : null;
  const labels = result ? verdictKey[result.verdict] : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mt: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <PublicIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>
          {tr('dashboard.cards.tariffClassifier.shippingCompliance.title') as string}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {tr('dashboard.cards.tariffClassifier.shippingCompliance.subtitle') as string}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label={
            tr('dashboard.cards.tariffClassifier.shippingCompliance.destinationLabel') as string
          }
          value={destination}
          onChange={(e) => setDestination(e.target.value.toUpperCase().slice(0, 2))}
          placeholder="DE"
          size="small"
          helperText={
            tr('dashboard.cards.tariffClassifier.shippingCompliance.destinationHelper') as string
          }
          inputProps={{ maxLength: 2, style: { textTransform: 'uppercase', letterSpacing: 2 } }}
          sx={{ width: 200 }}
          disabled={loading}
        />
        <Button
          variant="contained"
          onClick={handleCheck}
          disabled={loading || !isValidCountry}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PublicIcon />}
          sx={{ textTransform: 'none', borderRadius: 1.5 }}
        >
          {loading
            ? (tr('dashboard.cards.tariffClassifier.shippingCompliance.checking') as string)
            : (tr('dashboard.cards.tariffClassifier.shippingCompliance.checkButton') as string)}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && color && VerdictIcon && labels && (
        <Box sx={{ mt: 2.5 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              // When caveats follow, keep the verdict card visually quiet
              // (white background) so the amber caveat alert below dominates.
              // Without caveats, fill with the verdict's tint for a stronger
              // "this IS the answer" feel.
              bgcolor: hasCaveats ? 'background.paper' : `${color}.50`,
              border: '2px solid',
              borderColor: `${color}.main`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <VerdictIcon color={color} sx={{ fontSize: 36 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={800} color={`${color}.dark`}>
                {tr(labels.label) as string}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{
                  fontFamily: 'monospace',
                  letterSpacing: 1,
                  color: 'text.primary',
                  mt: 0.25,
                }}
              >
                {result.commodity_code} → {result.country_code}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tr(labels.helper) as string}
              </Typography>
            </Box>
          </Box>

          {(() => {
            const categories = deriveCategories(result);
            if (!categories.length) return null;
            return (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {categories.map((cat) => {
                  const Icon = CATEGORY_ICON[cat];
                  const label = tr(CATEGORY_LABEL_KEY[cat]) as string;
                  return (
                    <Chip
                      key={cat}
                      icon={<Icon />}
                      label={label}
                      color={CATEGORY_COLOR[cat]}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  );
                })}
              </Box>
            );
          })()}

          {hasCaveats && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mt: 2, borderRadius: 1.5, alignItems: 'flex-start' }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                {tr('dashboard.cards.tariffClassifier.shippingCompliance.caveatBanner') as string}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {tr('dashboard.cards.tariffClassifier.shippingCompliance.caveatBannerHelper') as string}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(result.caveats as Caveat[]).map((c) => {
                  const baseKey = `dashboard.cards.tariffClassifier.shippingCompliance.caveat.${c.code}`;
                  const title = tr(`${baseKey}.title`) as string;
                  const body = tr(`${baseKey}.body`) as string;
                  // If the i18n key is unknown the lookup returns the key itself;
                  // detect that and fall back to the backend `detail`.
                  const titleResolved = title.startsWith('dashboard.') ? c.code : title;
                  const bodyResolved = body.startsWith('dashboard.') ? c.detail : body;
                  return (
                    <Box key={c.code}>
                      <Typography variant="body2" fontWeight={700}>
                        {titleResolved}
                      </Typography>
                      <Typography variant="body2">{bodyResolved}</Typography>
                      {c.detail && bodyResolved !== c.detail && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {c.detail}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <MeasureList
              title={tr('dashboard.cards.tariffClassifier.shippingCompliance.importTitle') as string}
              measures={result.import_restrictions}
              t={tr}
            />
            <MeasureList
              title={tr('dashboard.cards.tariffClassifier.shippingCompliance.exportTitle') as string}
              measures={result.export_restrictions}
              t={tr}
            />
          </Box>

          {result.required_documents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                <DocIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                {
                  tr('dashboard.cards.tariffClassifier.shippingCompliance.documentsHeading', {
                    count: result.required_documents.length,
                  }) as string
                }
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                {result.required_documents.map((doc: DocumentDetail) => {
                  const tip = doc.description || doc.requirement || '';
                  const chip = (
                    <Chip
                      key={doc.code}
                      label={doc.code}
                      size="small"
                      variant="outlined"
                      sx={tip ? { cursor: 'help' } : undefined}
                    />
                  );
                  return tip ? (
                    <Tooltip key={doc.code} title={tip} arrow placement="top">
                      {chip}
                    </Tooltip>
                  ) : (
                    chip
                  );
                })}
              </Box>
            </Box>
          )}

          {result.legal_references.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                <LegalIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                {
                  tr('dashboard.cards.tariffClassifier.shippingCompliance.legalHeading', {
                    count: result.legal_references.length,
                  }) as string
                }
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                {result.legal_references.map((la) => (
                  la.url ? (
                    <Link
                      key={la.regulation_code}
                      href={la.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{ '&:hover .MuiChip-root': { borderColor: 'primary.main', color: 'primary.main' } }}
                    >
                      <Chip
                        label={la.regulation_code}
                        size="small"
                        variant="outlined"
                        clickable
                        icon={<OpenInNewIcon sx={{ fontSize: '0.85rem' }} />}
                        sx={{ fontFamily: 'monospace', color: 'primary.main', borderColor: 'primary.light' }}
                      />
                    </Link>
                  ) : (
                    <Chip
                      key={la.regulation_code}
                      label={la.regulation_code}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  )
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ShippingVerdictPanel;
